/**
 * MissionCard Component
 * 
 * Displays mission details in a card format
 */

import { FC } from 'react';
import { FileText, Download } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge, StatusType } from '@/components/StatusBadge';
import type { Mission } from '../types/missionHistory.types';

interface MissionCardProps {
    mission: Mission;
    onViewReport?: (mission: Mission) => void;
}

export const MissionCard: FC<MissionCardProps> = ({ mission, onViewReport }) => {
    return (
        <Card className="bg-slate-900 border-slate-700 hover:border-slate-600 transition-colors">
            <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-white font-medium">{mission.name}</h3>
                            <StatusBadge status={mission.status as StatusType} />
                        </div>
                        <p className="text-emerald-400 text-sm">Mission ID: {mission.id}</p>
                    </div>
                    <Button 
                        variant="outline" 
                        size="sm"
                        className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
                        onClick={() => onViewReport?.(mission)}
                    >
                        <FileText className="w-4 h-4 mr-2" />
                        View Report
                        <Download className="w-4 h-4 ml-2" />
                    </Button>
                </div>
                
                <div className="grid grid-cols-5 gap-4 text-sm">
                    <div>
                        <p className="text-slate-500 mb-1">AI Model:</p>
                        <p className="text-white">{mission.aiModel}</p>
                    </div>
                    <div>
                        <p className="text-slate-500 mb-1">Date:</p>
                        <p className="text-white">{mission.date}</p>
                        <p className="text-slate-500 mt-2 mb-1">Distance:</p>
                        <p className="text-white">{mission.distance}</p>
                    </div>
                    <div>
                        <p className="text-slate-500 mb-1">Duration:</p>
                        <p className="text-emerald-400">{mission.duration}</p>
                        <p className="text-slate-500 mt-2 mb-1">Max Altitude:</p>
                        <p className="text-emerald-400">{mission.maxAltitude}</p>
                    </div>
                    <div>
                        <p className="text-slate-500 mb-1">Drone:</p>
                        <p className="text-emerald-400">{mission.drone}</p>
                        <p className="text-slate-500 mt-2 mb-1">Detections:</p>
                        <p className="text-emerald-400">{mission.detections}</p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
